import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/primitives/Card";
import { StatusPill } from "@/components/primitives/StatusPill";
import { Input } from "@/components/primitives/Input";
import { useHealth } from "@/hooks/useHealth";
import { STYLE_GUIDE } from "@/constants/testIds";

const palette = [
  { name: "BG", hex: "#05070D", role: "Sfondo dominante" },
  { name: "PANEL", hex: "#0C1220", role: "Superfici / card" },
  { name: "ACCENT / CYAN", hex: "#2E7BFF", role: "Accento elettrico" },
  { name: "TEXT", hex: "#E6EDF3", role: "Testo primario" },
  { name: "MUTED", hex: "#7A8699", role: "Testo secondario" },
  { name: "SUCCESS", hex: "#3DDC84", role: "Stato positivo" },
  { name: "DANGER", hex: "#FF5C6C", role: "Stato di errore" },
];

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-14">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-semibold uppercase tracking-wide text-foreground">
          {title}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

function Swatch({ name, hex, role }: { name: string; hex: string; role: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button
      onClick={copy}
      className="group flex items-center gap-3 rounded-lg border border-border bg-[#0C1220] p-3 text-left transition-colors hover:border-[#2E7BFF]/50"
    >
      <span
        className="h-11 w-11 shrink-0 rounded-md border border-white/10"
        style={{ backgroundColor: hex }}
      />
      <span className="min-w-0 flex-1">
        <span className="block font-display text-xs font-semibold uppercase tracking-wide text-foreground">
          {name}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{role}</span>
      </span>
      <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
        {hex}
        {copied ? (
          <Check className="h-3 w-3 text-[#3DDC84]" />
        ) : (
          <Copy className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </span>
    </button>
  );
}

export default function StyleGuide() {
  const health = useHealth();

  return (
    <Layout>
      <main data-testid={STYLE_GUIDE.page} className="mx-auto max-w-5xl px-6 pb-24 pt-14">
        {/* Header */}
        <div className="mb-14 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold uppercase tracking-wide text-foreground sm:text-5xl">
              Style Guide
            </h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Palette, tipografia e componenti primitivi. Sobrio e leggibile:
              la decorazione resta al minimo.
            </p>
          </div>
          <div data-testid={STYLE_GUIDE.healthBadge}>
            {health.loading ? (
              <StatusPill tone="cyan" pulse>
                Verifica /health
              </StatusPill>
            ) : health.dbConnected ? (
              <StatusPill tone="success">/health OK · Mongo connesso</StatusPill>
            ) : (
              <StatusPill tone="danger">/health non disponibile</StatusPill>
            )}
          </div>
        </div>

        {/* Palette */}
        <Section
          title="Palette"
          subtitle="Dark mode dominante con cyan elettrico come accento. Clicca per copiare l'HEX."
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {palette.map((c) => (
              <Swatch key={c.name} {...c} />
            ))}
          </div>
        </Section>

        {/* Typography */}
        <Section
          title="Tipografia"
          subtitle="Display = Rajdhani (titoli/branding) · Body = Inter (testi e pulsanti)."
        >
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardDescription>Display · Rajdhani</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="font-display text-4xl font-bold uppercase tracking-wide text-foreground glow-cyan">
                  Simulation Studios
                </p>
                <p className="font-display text-xl font-semibold uppercase tracking-wide text-foreground">
                  Scena Cinematica
                </p>
                <p className="text-xs text-muted-foreground">
                  Uso: titoli e branding.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardDescription>Body · Inter</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-base leading-relaxed text-foreground">
                  Testo del corpo in Inter, ottimizzato per la leggibilità
                  immediata e la gerarchia visiva chiara.
                </p>
                <p className="text-sm text-muted-foreground">
                  Testo secondario in tono muted per note e descrizioni.
                </p>
              </CardContent>
            </Card>
          </div>
        </Section>

        {/* Buttons */}
        <Section
          title="Bottoni"
          subtitle="Primario skew cyan (CTA principale) · Secondario outline · Ghost."
        >
          <Card>
            <CardContent className="flex flex-wrap items-center gap-4">
              <Button variant="primary" data-testid={STYLE_GUIDE.btnGenera}>
                GENERA
              </Button>
              <Button variant="secondary" data-testid={STYLE_GUIDE.btnImporta}>
                IMPORTA
              </Button>
              <Button variant="ghost" data-testid={STYLE_GUIDE.btnAnnulla}>
                ANNULLA
              </Button>
            </CardContent>
          </Card>
        </Section>

        {/* Status pills */}
        <Section
          title="Status Pill"
          subtitle="Verde (pronto) · cyan (in corso) · rosso (errore)."
        >
          <Card>
            <CardContent className="flex flex-wrap items-center gap-4">
              <StatusPill tone="success" data-testid={STYLE_GUIDE.pillPronto}>
                PRONTO
              </StatusPill>
              <StatusPill tone="cyan" pulse data-testid={STYLE_GUIDE.pillInGenerazione}>
                IN GENERAZIONE
              </StatusPill>
              <StatusPill tone="danger" data-testid={STYLE_GUIDE.pillNonRiuscito}>
                NON RIUSCITO
              </StatusPill>
            </CardContent>
          </Card>
        </Section>

        {/* Input + Card */}
        <Section
          title="Input & Card"
          subtitle="Campo con focus cyan e card scura a bordo sottile."
        >
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Campo di input</CardTitle>
                <CardDescription>Focus con anello cyan controllato.</CardDescription>
              </CardHeader>
              <CardContent>
                <Input
                  label="Titolo progetto"
                  name="project"
                  placeholder="Es. Il viaggio del protagonista"
                  data-testid={STYLE_GUIDE.inputDemo}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Card standard</CardTitle>
                <CardDescription>Superficie scura, bordo sottile.</CardDescription>
              </CardHeader>
              <CardContent>
                Contenitore neutro per contenuti e controlli, senza decorazioni
                aggiuntive per mantenere la leggibilità.
              </CardContent>
            </Card>
          </div>
        </Section>

        <footer className="mt-16 border-t border-border pt-6 text-xs text-muted-foreground">
          Simulation Studios · Step 0 — Scaffold & Design System
        </footer>
      </main>
    </Layout>
  );
}
